import { ParamsType, ProTable } from "@ant-design/pro-components";
import {
  ActionType,
  ProColumns,
  ProTableProps,
} from "@ant-design/pro-table/es/typing";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FormOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import React, { useMemo, useRef } from "react";
import { Button, ButtonProps, FormInstance } from "antd";
import { AnyObject } from "@triare/auth-redux";
import { useNavigate } from "react-router";
import useCurrentLocation from "../../../hooks/router";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

export interface TableOnRowSelect {
  params?: Record<string, string>;
  selectedRows?: string[];
  onRowSelection?: ((selectedRows: string[]) => void) | undefined;
}

export interface TableListItem {
  id: string | number;
  key: string | number;
}

export interface TableProps<T, U, V> extends ProTableProps<T, U, V> {
  actions?: (
    | [
      (props: {
        text: React.ReactNode;
        record: T;
        id: number;
        className: string;
      }) => React.ReactNode,
    ]
    | [
      (
        props: {
          text: React.ReactNode;
          record: T;
          id: number;
          className: string;
        } & AnyObject,
      ) => React.ReactNode,
      AnyObject,
    ]
    | [
      string,
      (props: {
        text: React.ReactNode;
        record: T;
        id: number;
      }) => ProColumns<T, V>,
    ]
    | [
      string,
      (props: {
        text: React.ReactNode;
        record: T;
        id: number;
      }) => ProColumns<T, V>,
      (props: { text: React.ReactNode; record: T; id: number }) => boolean,
    ]
  )[];
  /** Used to clear filters & table data */
  resetKey: () => void;
}

export const listAction = {
  form: (props: ButtonProps) => (
    <Button key="form" type="text" {...props} icon={<FormOutlined />} />
  ),
  edit: (props: ButtonProps) => (
    <Button key="edit" type="text" {...props} icon={<EditOutlined />} />
  ),
  delete: (props: ButtonProps) => (
    <Button key="delete" type="text" {...props} icon={<DeleteOutlined />} />
  ),
  reload: (props: ButtonProps) => (
    <Button key="reload" type="text" {...props} icon={<ReloadOutlined />} />
  ),
  copy: (props: ButtonProps) => (
    <Button key="copy" type="text" {...props} icon={<CopyOutlined />} />
  ),
  download: (props: ButtonProps) => (
    <Button key="download" type="text" {...props} icon={<DownloadOutlined />} />
  ),
  view: (props: ButtonProps) => (
    <Button key="view" type="text" {...props} icon={<EyeOutlined />} />
  ),
};

function Table<
  T extends TableListItem,
  Params extends ParamsType = ParamsType,
  ValueType = "text",
>({
  options,
  pagination,
  columns,
  actions,
  form,
  search,
  resetKey,
  ...props
}: TableProps<T, Params, ValueType>) {
  const formRef = useRef<FormInstance>(undefined);
  const actionRef = useRef<ActionType>(undefined);
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const currentLocation = useCurrentLocation();

  const newColumns = useMemo(() => {
    const list = (columns || []).map((column) => {
      if (column.hideInSearch === true) {
        return column;
      }

      return column;
      // column.
    });

    if (actions) {
      list.push({
        key: "id",
        title: t("common.table.actions"),
        dataIndex: "id",
        hideInSearch: true,
        // width: actions?.length ? actions.length * 32 + 8 : 104,
        width: Math.max(actions.length * 32, 150),
        render: (text, record, id) => (
          <div className={styles.actions}>
            {actions
              .map((action) => {
                if (typeof action[0] === "string") {
                  const [key, createData, check] = action;

                  if (
                    (!check || check({ text, record, id })) &&
                    typeof createData === "function"
                  ) {
                    const data = createData({ text, record, id });

                    // @ts-ignore - use the action icon in the list or use a custom element
                    return listAction[key] ? listAction[key](data) : data;
                  }
                } else {
                  const [Component, additionalProps] = action;

                  return (
                    <Component
                      key={Component.name}
                      id={id}
                      text={text}
                      record={record}
                      className={styles.actionBtn}
                      {...additionalProps}
                    />
                  );
                }

                return null;
              })
              .filter((value) => value !== null)}
          </div>
        ),
      });
    }

    return list;
    // TODO upd deps
  }, [i18n.language]);

  return (
    <ProTable<T, Params, ValueType>
      formRef={formRef}
      actionRef={actionRef}
      className={styles.table}
      rowKey="key"
      dateFormatter="string"
      pagination={{
        defaultPageSize: 100,
        size: "default",
        // showQuickJumper: true,
        showSizeChanger: { showSearch: false },
        // showTotal: true,
        showTotal: (total) => `Total ${total} items`,
        pageSizeOptions: [10, 20, 30, 50, 100],
        ...pagination,
      }}
      onReset={() => {
        const isSyncToUrl = form?.syncToUrl;

        if (actionRef.current && formRef.current) {
          actionRef.current?.reset?.();
          setTimeout(() => {
            resetKey();
            if (isSyncToUrl) {
              navigate(currentLocation, { replace: true });
            }
          }, 0);
          // formRef.current.resetFields?.();
          // const newValues = Object.keys(formRef.current?.getFieldsValue())
          //   .map((name) => ({
          //     name,
          //     value: undefined,
          //   }));
          //
          // formRef.current?.setFieldsValue(newValues);
        }
      }}
      columns={newColumns}
      options={{
        density: true,
        search: false,
        reloadIcon: <HistoryOutlined />,
        ...(options || {}),
      }}
      search={
        search
          ? {
            labelWidth: "auto",
            filterType: "query",
            searchText: t("common.table.filter"),
            resetText: t("common.table.clear"),
            ...search,
          }
          : false
      }
      form={{
        syncToUrl: true,
        ...form,
      }}
      {...props}
    />
  );
}

export default Table;
